import { BLOCKS } from '@contentful/rich-text-types'
import { AccordionContext } from '@contexts/accordion'
import {
  AssetOptionsContext,
  type AssetOptionsContextProps,
  DEFAULT_MAX_SVG_HEIGHT,
  DEFAULT_MIN_SVG_WIDTH,
} from '@contexts/asset-options'
import { ContentType } from '@entries/content-types'
import ContentfulAsset from '@entries/contentful-asset'
import { withDisplayName } from '@hocs/with-display-name'
import { children } from '@render-node/utils/children'
import { Entry } from '@resolvers/entry'
import RichText from '@resolvers/rich-text'
import { defaultRichTextFieldOptionsDeprecated } from '@resolvers/rich-text/default-rich-text-field-options-deprecated'
import { Accordion } from '@ui/accordion'
import { Hyperlink } from '@ui/hyperlink'
import { graphql } from 'gatsby'
import React, { useCallback, useContext } from 'react'

export const summaryAccordionAssetOptions: AssetOptionsContextProps = {
  width: '100%',
  height: 'auto',
  objectFit: 'contain',
  minSvgWidth: DEFAULT_MIN_SVG_WIDTH,
  maxSvgHeight: DEFAULT_MAX_SVG_HEIGHT,
  alt: '',
}

const ContentfulContentArticleSummaryAccordionItem: Utils.EntryFC<
  Queries.ContentfulContentArticleSummaryAccordionItemFragment
> = ({
  content: {
    id,
    contentArticleTitle: title,
    image,
    content,
    contentLinkUrl,
    contentLinkText,
  },
  fieldInspectorProps,
}) => {
  const { selectedId, setSelectedId } = useContext(AccordionContext)

  const onChange = useCallback(
    (expanded: boolean) => {
      setSelectedId(expanded ? id : null)
    },
    [id, setSelectedId]
  )

  return (
    <Accordion
      expanded={selectedId === id}
      id={id}
      title={
        <RichText
          {...fieldInspectorProps({
            fieldId: 'title',
          })}
          content={title}
          options={{
            ...defaultRichTextFieldOptionsDeprecated,
            renderNode: {
              ...defaultRichTextFieldOptionsDeprecated.renderNode,
              [BLOCKS.EMBEDDED_ASSET]: {
                [ContentType.ContentfulAsset]: ContentfulAsset,
              },
              [BLOCKS.PARAGRAPH]: children,
            },
          }}
        />
      }
      onChange={onChange}
    >
      {content && (
        <RichText
          {...fieldInspectorProps({
            fieldId: 'content',
          })}
          content={content}
          options={defaultRichTextFieldOptionsDeprecated}
        />
      )}
      {contentLinkText && contentLinkUrl && (
        <Hyperlink
          {...fieldInspectorProps({
            fieldId: 'contentLinkText',
          })}
          href={contentLinkUrl}
        >
          {contentLinkText}
        </Hyperlink>
      )}
      {Boolean(image) && (
        <AssetOptionsContext.Provider value={summaryAccordionAssetOptions}>
          <div
            className="block pt-4 laptop:hidden"
            {...fieldInspectorProps({
              fieldId: 'image',
            })}
          >
            <Entry
              content={image}
              mapping={{
                [ContentType.ContentfulAsset]: ContentfulAsset,
              }}
            />
          </div>
        </AssetOptionsContext.Provider>
      )}
    </Accordion>
  )
}

export const query = graphql`
  fragment ContentfulContentArticleSummaryAccordionItem on Contentful4TmD9EihHakmKMkEwKaUwQ {
    ...ContentfulEntry
    contentArticleTitle: title {
      ...ContentfulContentArticleTitle
    }
    content {
      ...ContentfulContentArticleContent
    }
    image {
      ...ContentfulDefaultAsset
    }
    contentLinkText
    contentLinkUrl
  }
`

export default withDisplayName(
  ContentfulContentArticleSummaryAccordionItem,
  'ContentfulContentArticleSummaryAccordionItem'
)
